import { useState } from "react";
import { T, fontMono } from "../theme";
import { addWeeks, fmtDay, fmtMoney, isoDate, parseDate, todayStr } from "../engine/dates";
import { addDocSpend, deleteDocSpend, updateDocSpend } from "../lib/data";

/**
 * The cost of delivering a piece of income, attached to the income itself.
 *
 * Two shapes, both common and both badly served by a single date:
 *   media buying — one budget spent evenly across several weeks
 *   production   — one payment leaving on a date some weeks out
 *
 * Both are the same record: an amount, a start, and how many weeks to spread it
 * over. One week is a lump.
 *
 * Method matters as much as amount, because it decides WHEN the money leaves the
 * bank: a supplier invoice goes on its date, an AMEX charge not until the card
 * is paid, which can be weeks later.
 */

const BLANK = () => ({
  description: "",
  supplier_name: "",
  amount: "",
  method: "amex",
  category: "media",
  start_date: isoDate(addWeeks(parseDate(todayStr()), 4)),
  spread_weeks: 1,
  includes_vat: true,
  amount_is_net: true,
  vat_rate: 20,
});

/** VAT is per cost, not per invoice: plenty of spend carries none even when the
 *  income does — overseas suppliers, some freelancers, insurance. */
export function netOf(r) {
  const amount = Number(r.amount) || 0;
  if (r.includes_vat === false) return amount;
  if (r.amount_is_net) return amount;
  return amount / (1 + Number(r.vat_rate ?? 20) / 100);
}

/** What leaves the bank. A supplier price typed in net has VAT added to it. */
export function grossOf(r) {
  const amount = Number(r.amount) || 0;
  if (r.includes_vat === false || !r.amount_is_net) return amount;
  return amount * (1 + Number(r.vat_rate ?? 20) / 100);
}

function SpendForm({ value, onChange, onSubmit, onCancel, busy, submitLabel, amexUnconfigured }) {
  const f = value;
  const set = (patch) => onChange({ ...f, ...patch });
  const input = (key, extra = {}) => (
    <input
      value={f[key] ?? ""}
      onChange={(e) => set({ [key]: e.target.value })}
      style={{ fontSize: 11, padding: "3px 5px", marginTop: 2, ...extra }}
    />
  );
  const lbl = { fontSize: 10, color: T.muted };

  return (
    <div style={{ marginTop: 10, background: T.panelSoft, borderRadius: 8, padding: 10 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <label style={lbl}>
          What
          <br />
          {input("description", { width: 130 })}
        </label>
        <label style={lbl}>
          Supplier
          <br />
          {input("supplier_name", { width: 110 })}
        </label>
        <label style={lbl}>
          Total amount
          <br />
          {input("amount", { width: 84, fontFamily: fontMono })}
        </label>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, alignItems: "flex-end" }}>
        <label style={lbl}>
          Paid by
          <br />
          <select
            value={f.method}
            onChange={(e) => set({ method: e.target.value })}
            style={{ fontSize: 11, padding: "3px 5px", marginTop: 2 }}
          >
            <option value="amex">AMEX</option>
            <option value="supplier">Supplier invoice</option>
            <option value="payroll">Payroll / freelance</option>
          </select>
        </label>
        <label style={lbl}>
          Category
          <br />
          <select
            value={f.category}
            onChange={(e) => set({ category: e.target.value })}
            style={{ fontSize: 11, padding: "3px 5px", marginTop: 2 }}
          >
            {["media", "suppliers", "staff", "software", "other"].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label style={lbl}>
          Starts
          <br />
          <input
            type="date"
            value={f.start_date}
            onChange={(e) => set({ start_date: e.target.value })}
            style={{ fontSize: 11, padding: "3px 5px", marginTop: 2, fontFamily: fontMono }}
          />
        </label>
        <label style={lbl}>
          Over (weeks)
          <br />
          <input
            type="number"
            min="1"
            max="52"
            value={f.spread_weeks}
            onChange={(e) => set({ spread_weeks: e.target.value })}
            style={{ width: 56, fontSize: 11, padding: "3px 5px", marginTop: 2, fontFamily: fontMono }}
          />
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
        <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}>
          <input
            type="checkbox"
            checked={f.includes_vat !== false}
            onChange={(e) => set({ includes_vat: e.target.checked })}
          />
          this cost has VAT on it
        </label>
        {f.includes_vat !== false && (
          <select
            value={f.amount_is_net ? "net" : "gross"}
            onChange={(e) => set({ amount_is_net: e.target.value === "net" })}
            style={{ fontSize: 11, padding: "3px 5px", fontFamily: "inherit" }}
          >
            <option value="net">and the amount above is before VAT</option>
            <option value="gross">and the amount above already includes it</option>
          </select>
        )}
        {f.includes_vat !== false && (
          <label style={lbl}>
            at
            <input
              type="number"
              min="0"
              max="100"
              value={f.vat_rate ?? 20}
              onChange={(e) => set({ vat_rate: e.target.value })}
              style={{ width: 46, fontSize: 11, padding: "3px 4px", marginLeft: 4, fontFamily: fontMono }}
            />
            %
          </label>
        )}
        <button
          onClick={onSubmit}
          disabled={busy || !Number(f.amount)}
          style={{
            marginLeft: "auto",
            padding: "5px 12px",
            borderRadius: 6,
            border: "none",
            background: Number(f.amount) ? T.accent : T.border,
            color: Number(f.amount) ? "#fff" : T.muted,
            fontSize: 11,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {busy ? "Saving…" : submitLabel}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            style={{
              border: `1px solid ${T.border}`,
              background: "transparent",
              borderRadius: 6,
              padding: "5px 10px",
              fontSize: 11,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
        )}
      </div>

      <div style={{ fontSize: 10, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
        {Number(f.spread_weeks) > 1 && Number(f.amount) > 0
          ? `${fmtMoney(Number(f.amount) / Number(f.spread_weeks))} a week for ${f.spread_weeks} weeks from ${fmtDay(f.start_date)}.`
          : "One payment on the start date. Set weeks above 1 to spread it."}
        {f.includes_vat === false
          ? " No VAT on this one, so the full amount counts as cost."
          : Number(f.amount) > 0
            ? ` ${fmtMoney(grossOf(f))} leaves the bank; ${fmtMoney(netOf(f))} of that is cost, and the VAT comes back.`
            : ""}
        {f.method === "amex" &&
          " On AMEX it hits the card on those dates and the bank when the statement is paid."}
        {f.method === "amex" && amexUnconfigured && (
          <span style={{ color: T.warn }}>
            {" "}
            AMEX isn't set up yet, so a statement on the 1st paid 25 days later is assumed.
          </span>
        )}
      </div>
    </div>
  );
}

export default function AttachedSpend({
  docSource,
  docId,
  income,
  netIncome,
  spend,
  onChanged,
  amexUnconfigured,
  readOnly = false,
}) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const rows = spend || [];
  // Two different totals, deliberately. Gross is what leaves the bank; net is
  // what the work actually costs, because the VAT comes back. Margin compares
  // net income with net cost — VAT is collected on someone else's behalf and is
  // not profit on either side.
  const totalGross = rows.reduce((s, r) => s + grossOf(r), 0);
  const totalNet = rows.reduce((s, r) => s + netOf(r), 0);
  const incomeNet = Number(netIncome) || 0;
  const margin = incomeNet - totalNet;

  const payload = (f) => ({
    description: f.description || null,
    supplier_name: f.supplier_name || null,
    amount: Number(f.amount),
    method: f.method,
    category: f.method === "payroll" ? "staff" : f.category,
    start_date: f.start_date,
    spread_weeks: Math.max(1, Number(f.spread_weeks) || 1),
    includes_vat: f.includes_vat !== false,
    amount_is_net: !!f.amount_is_net,
    vat_rate: Number(f.vat_rate ?? 20),
  });

  const save = async () => {
    if (!Number(form.amount)) return;
    setBusy(true);
    setError(null);
    try {
      if (editingId) await updateDocSpend(editingId, payload(form));
      else await addDocSpend({ doc_source: docSource, doc_id: String(docId), ...payload(form) });
      setAdding(false);
      setEditingId(null);
      setForm(BLANK());
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (r) => {
    setAdding(false);
    setEditingId(r.id);
    setForm({
      description: r.description || "",
      supplier_name: r.supplier_name || "",
      amount: String(r.amount),
      method: r.method,
      category: r.category || "media",
      start_date: r.start_date,
      spread_weeks: r.spread_weeks,
      includes_vat: r.includes_vat !== false,
      amount_is_net: !!r.amount_is_net,
      vat_rate: Number(r.vat_rate ?? 20),
    });
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await deleteDocSpend(id);
      if (editingId === id) setEditingId(null);
      await onChanged();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16, borderTop: `1px solid ${T.borderSoft}`, paddingTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>Spend attached to this</div>
        {readOnly ? null : (
        <button
          onClick={() => {
            setEditingId(null);
            setForm(BLANK());
            setAdding(!adding);
          }}
          style={{
            marginLeft: "auto",
            border: `1px solid ${T.border}`,
            background: "transparent",
            borderRadius: 6,
            padding: "3px 9px",
            fontSize: 11,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {adding ? "Cancel" : "+ Add cost"}
        </button>
        )}
      </div>

      {rows.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {rows.map((r) => (
            <div key={r.id} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "6px 0", fontSize: 11 }}>
                <div style={{ flex: 1 }}>
                  <div>{r.supplier_name || r.description || "Cost"}</div>
                  <div style={{ fontSize: 10, color: T.muted }}>
                    {r.method === "amex" ? "AMEX" : r.method === "payroll" ? "Payroll" : "Supplier"} ·{" "}
                    {r.includes_vat === false
                      ? "no VAT"
                      : `${fmtMoney(netOf(r))} + ${r.vat_rate ?? 20}% VAT`}{" "}
                    ·{" "}
                    {Number(r.spread_weeks) > 1
                      ? `${fmtMoney(grossOf(r) / r.spread_weeks)}/wk across ${r.spread_weeks} weeks from ${fmtDay(r.start_date)}`
                      : `one payment ${fmtDay(r.start_date)}`}
                  </div>
                </div>
                <div style={{ fontFamily: fontMono, color: T.cost }}>{fmtMoney(-grossOf(r))}</div>
                {readOnly ? null : (
                <>
                <button
                  onClick={() => (editingId === r.id ? setEditingId(null) : startEdit(r))}
                  disabled={busy}
                  title="Edit this cost"
                  style={{ border: "none", background: "none", color: T.muted, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}
                >
                  {editingId === r.id ? "close" : "edit"}
                </button>
                <button
                  onClick={() => remove(r.id)}
                  disabled={busy}
                  title="Remove"
                  style={{ border: "none", background: "none", color: T.muted, cursor: "pointer", fontSize: 13 }}
                >
                  ×
                </button>
                </>
                )}
              </div>
              {!readOnly && editingId === r.id && (
                <SpendForm
                  value={form}
                  onChange={setForm}
                  onSubmit={save}
                  onCancel={() => setEditingId(null)}
                  busy={busy}
                  submitLabel="Save changes"
                  amexUnconfigured={amexUnconfigured}
                />
              )}
            </div>
          ))}

          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 4, paddingTop: 6, fontSize: 11 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: T.muted }}>Cost out of the bank (gross)</span>
              <span style={{ fontFamily: fontMono, color: T.cost }}>{fmtMoney(-totalGross)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
              <span style={{ color: T.muted }}>Cost excl. VAT</span>
              <span style={{ fontFamily: fontMono, color: T.muted }}>{fmtMoney(-totalNet)}</span>
            </div>
            {incomeNet > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                  <span style={{ color: T.muted }}>Income excl. VAT</span>
                  <span style={{ fontFamily: fontMono, color: T.muted }}>{fmtMoney(incomeNet)}</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 4,
                    paddingTop: 4,
                    borderTop: `1px solid ${T.borderSoft}`,
                  }}
                >
                  <span style={{ color: T.muted }}>Margin · both excl. VAT</span>
                  <span
                    style={{ fontFamily: fontMono, fontWeight: 600, color: margin >= 0 ? T.income : T.cost }}
                  >
                    {fmtMoney(margin)}
                    <span style={{ color: T.muted, fontWeight: 400 }}>
                      {" "}
                      ({Math.round((margin / incomeNet) * 100)}%)
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {!rows.length && !adding && (
        <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>
          Nothing attached yet — add the media, production or freelance cost that delivers this.
        </div>
      )}

      {error && <div style={{ color: T.cost, fontSize: 11, marginTop: 6 }}>{error}</div>}

      {!readOnly && adding && (
        <SpendForm
          value={form}
          onChange={setForm}
          onSubmit={save}
          busy={busy}
          submitLabel="Attach"
          amexUnconfigured={amexUnconfigured}
        />
      )}
    </div>
  );
}
